import { useCallback, useEffect, useState } from 'react'
import { App, Button, Form, Input, Modal, Popconfirm, Select, Space, Switch, Table, Tag, message } from 'antd'
import { adminApi, type PageResult } from '../../api'
import { PageHeader, tablePagination, useServerPagination } from '../../components/ui'

type User = {
  id: number
  username: string
  display_name: string
  is_active: boolean
  is_superuser: boolean
  role_ids: number[]
}
type Role = { id: number; name: string }

export default function Users() {
  const { modal } = App.useApp()
  const { page, pageSize, handlePaginationChange, paginationOptions } = useServerPagination()
  const [total, setTotal] = useState(0)
  const [rows, setRows] = useState<User[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [currentUserId, setCurrentUserId] = useState<number | null>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([])
  const [deleting, setDeleting] = useState(false)
  const [open, setOpen] = useState(false)
  const [ed, setEd] = useState<User | null>(null)
  const [form] = Form.useForm()

  const load = useCallback(
    (p: number, ps: number) => {
      adminApi
        .get<PageResult<User>>('/admin/users', { params: { page: p, page_size: ps } })
        .then((r) => {
          setRows(r.data.items)
          setTotal(r.data.total)
          setSelectedRowKeys([])
        })
    },
    [],
  )

  useEffect(() => {
    load(page, pageSize)
    adminApi
      .get<PageResult<Role>>('/admin/roles', { params: { page: 1, page_size: 100 } })
      .then((r) => setRoles(r.data.items))
    adminApi
      .get<{ user: { id: number } }>('/admin/auth/me')
      .then((r) => setCurrentUserId(r.data.user?.id ?? null))
      .catch(() => setCurrentUserId(null))
  }, [page, pageSize, load])

  const submit = () => {
    void form.validateFields().then((v) => {
      const body = { ...v, role_ids: v.role_ids || [] }
      const req = ed
        ? adminApi.put(`/admin/users/${ed.id}`, body)
        : adminApi.post('/admin/users', body)
      req
        .then(() => {
          message.success('已保存')
          setOpen(false)
          load(page, pageSize)
        })
        .catch((e) => message.error(e.message))
    })
  }

  const removeUsers = (ids: number[]) => {
    if (!ids.length) {
      message.warning('请先选择要删除的用户')
      return
    }
    setDeleting(true)
    const req =
      ids.length === 1
        ? adminApi.delete(`/admin/users/${ids[0]}`)
        : adminApi.post('/admin/users/batch-delete', { ids })
    req
      .then((r) => {
        const deleted = r.data.deleted ?? ids.length
        message.success(`已删除 ${deleted} 个用户`)
        setSelectedRowKeys([])
        load(page, pageSize)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setDeleting(false))
  }

  const confirmBatchDelete = () => {
    if (!selectedRowKeys.length) {
      message.warning('请先勾选要删除的用户')
      return
    }
    modal.confirm({
      title: `确定删除选中的 ${selectedRowKeys.length} 个用户？`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => removeUsers(selectedRowKeys),
    })
  }

  const canDeleteUser = (user: User) => currentUserId == null || user.id !== currentUserId

  return (
    <div>
      <PageHeader title="用户管理" />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8, gap: 8 }}>
        <Button danger disabled={!selectedRowKeys.length || deleting} onClick={confirmBatchDelete}>
          批量删除
        </Button>
        <Button
          type="primary"
          onClick={() => {
            setEd(null)
            form.resetFields()
            form.setFieldsValue({ is_active: true, is_superuser: false, role_ids: [] })
            setOpen(true)
          }}
        >
          新增用户
        </Button>
      </div>
      <Table
        rowKey="id"
        size="small"
        dataSource={rows}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys.map(Number)),
          getCheckboxProps: (row) => ({ disabled: !canDeleteUser(row) }),
        }}
        pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
        columns={[
          { title: '用户名', dataIndex: 'username' },
          { title: '显示名', dataIndex: 'display_name' },
          {
            title: '状态',
            render: (_, r) => (r.is_active ? <Tag color="green">启用</Tag> : <Tag>禁用</Tag>),
          },
          {
            title: '超管',
            render: (_, r) => (r.is_superuser ? <Tag color="blue">是</Tag> : <Tag>否</Tag>),
          },
          {
            title: '操作',
            render: (_, r) => (
              <Space size="small">
                <Button
                  size="small"
                  onClick={() => {
                    setEd(r)
                    form.setFieldsValue({ ...r, password: '' })
                    setOpen(true)
                  }}
                >
                  编辑
                </Button>
                <Popconfirm
                  title={`确定删除用户「${r.display_name || r.username}」？`}
                  okText="删除"
                  okButtonProps={{ danger: true }}
                  cancelText="取消"
                  onConfirm={() => removeUsers([r.id])}
                  disabled={!canDeleteUser(r)}
                >
                  <Button size="small" danger disabled={!canDeleteUser(r) || deleting}>
                    删除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <Modal title={ed ? '编辑用户' : '新增用户'} open={open} onOk={submit} onCancel={() => setOpen(false)}>
        <Form form={form} layout="vertical">
          <Form.Item name="username" label="用户名" rules={[{ required: true }]}>
            <Input disabled={!!ed} />
          </Form.Item>
          <Form.Item name="password" label={ed ? '新密码（不填不改）' : '密码'} rules={ed ? [] : [{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="display_name" label="显示名">
            <Input />
          </Form.Item>
          <Form.Item name="role_ids" label="角色">
            <Select mode="multiple" options={roles.map((r) => ({ value: r.id, label: r.name }))} />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="is_superuser" label="超级管理员" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
